import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertCircle, Check, CheckSquare, FolderPlus, Layers3, MessageCircle, Pencil, Plus, Trash2, UserPlus, X } from 'lucide-react';
import { useAuth } from '../../context';
import {
  apiAddTodoItem, apiAddTodoSection, apiCreateTodoList, apiDeleteTodoItem, apiDeleteTodoList,
  apiDeleteTodoSection, apiGetTeam, apiGetTodos, apiUpdateTodoItem, apiUpdateTodoList, apiUpdateTodoSection,
} from '../../api';
import Thread from '../Thread/Thread';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import ModalShell from '../Modal/ModalShell';
import { PersonAvatar } from '../People/PeopleUI';
import { canDeleteTeamContent } from '../../permissions';
import './TodoBoard.css';

const idOf = (value) => String(value?._id || value || '');
const tempId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const scrollToTodo = (attribute, id) => window.setTimeout(() => {
  document.querySelector(`[${attribute}="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}, 30);

export default function TodoBoard({ projectId = '', embedded = false }) {
  const { clientId } = useOutletContext();
  const { jwtToken, user } = useAuth();
  const [lists, setLists] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [newListTitle, setNewListTitle] = useState('');
  const [addingList, setAddingList] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [sectionForms, setSectionForms] = useState({});
  const [sectionDrafts, setSectionDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [headingEditor, setHeadingEditor] = useState(null);
  const [taskEditor, setTaskEditor] = useState(null);
  const [commentTarget, setCommentTarget] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const setOperation = (key, value) => setBusy((current) => {
    const next = { ...current };
    if (value) next[key] = true;
    else delete next[key];
    return next;
  });
  const updateList = useCallback((listId, updater) => {
    setLists((current) => current.map((list) => idOf(list) === idOf(listId) ? updater(list) : list));
  }, []);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [todoData, teamData] = await Promise.all([
        apiGetTodos(jwtToken, clientId, projectId), apiGetTeam(jwtToken).catch(() => []),
      ]);
      setLists(Array.isArray(todoData) ? todoData : []);
      setTeam(Array.isArray(teamData) ? teamData : []);
    } catch (err) {
      setLoadError(err.message || 'The to-do board could not be loaded.');
    } finally { setLoading(false); }
  }, [jwtToken, clientId, projectId]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const createList = async (event) => {
    event.preventDefault();
    const title = newListTitle.trim();
    if (!title || addingList) return;
    const optimisticId = tempId('new-list');
    const optimisticList = {
      _id: optimisticId, title, items: [], sections: [], createdBy: user?.id || user?._id,
      createdByRole: user?.role, _optimistic: true,
    };
    setError(''); setAddingList(true); setNewListTitle('');
    setLists((current) => [...current, optimisticList]);
    scrollToTodo('data-todo-list', optimisticId);
    try {
      const created = await apiCreateTodoList(jwtToken, { client: clientId, project: projectId || null, title });
      setLists((current) => current.map((list) => idOf(list) === optimisticId
        ? { ...created, sections: created.sections || [], items: created.items || [] } : list));
    } catch (err) {
      setLists((current) => current.filter((list) => idOf(list) !== optimisticId));
      setNewListTitle(title);
      setError(err.message || 'The list could not be created.');
    } finally { setAddingList(false); }
  };

  const createSection = async (event, list) => {
    event.preventDefault();
    const listId = idOf(list);
    const title = String(sectionDrafts[listId] || '').trim();
    const key = `section-add:${listId}`;
    if (!title || busy[key] || list._optimistic) return;
    const optimisticId = tempId('new-section');
    setError(''); setOperation(key, true);
    setSectionDrafts((current) => ({ ...current, [listId]: '' }));
    updateList(listId, (current) => ({ ...current, sections: [...(current.sections || []), { _id: optimisticId, title, _optimistic: true }] }));
    try {
      const result = await apiAddTodoSection(jwtToken, listId, { title });
      updateList(listId, (current) => ({ ...current, sections: current.sections.map((section) => idOf(section) === optimisticId ? result.section : section) }));
      setDrafts((current) => ({ ...current, [listId]: { ...(current[listId] || {}), sectionId: idOf(result.section) } }));
      setSectionForms((current) => ({ ...current, [listId]: false }));
    } catch (err) {
      updateList(listId, (current) => ({ ...current, sections: current.sections.filter((section) => idOf(section) !== optimisticId) }));
      setSectionDrafts((current) => ({ ...current, [listId]: title }));
      setError(err.message || 'The section could not be created.');
    } finally { setOperation(key, false); }
  };

  const addItem = async (event, list) => {
    event.preventDefault();
    const listId = idOf(list);
    const draft = drafts[listId] || {};
    const text = String(draft.text || '').trim();
    const key = `item-add:${listId}`;
    if (!text || busy[key] || list._optimistic) return;
    const optimisticId = tempId('new-item');
    const person = team.find((member) => idOf(member.id || member) === idOf(draft.assignedTo));
    const optimisticItem = {
      _id: optimisticId, text, done: false, assignedTo: draft.assignedTo || null,
      assignedToName: person?.name || '', sectionId: draft.sectionId || null,
      createdBy: user?.id || user?._id, createdByRole: user?.role, messageCount: 0, _optimistic: true,
    };
    setError(''); setOperation(key, true);
    setDrafts((current) => ({ ...current, [listId]: { ...draft, text: '' } }));
    updateList(listId, (current) => ({ ...current, items: [...(current.items || []), optimisticItem] }));
    scrollToTodo('data-todo-item', optimisticId);
    try {
      const result = await apiAddTodoItem(jwtToken, listId, { text, assignedTo: draft.assignedTo || null, sectionId: draft.sectionId || null });
      updateList(listId, (current) => ({ ...current, items: current.items.map((item) => idOf(item) === optimisticId ? { ...result.item, messageCount: 0 } : item) }));
    } catch (err) {
      updateList(listId, (current) => ({ ...current, items: current.items.filter((item) => idOf(item) !== optimisticId) }));
      setDrafts((current) => ({ ...current, [listId]: { ...draft, text } }));
      setError(err.message || 'The to-do could not be created.');
    } finally { setOperation(key, false); }
  };

  const toggleDone = async (listId, item) => {
    const itemId = idOf(item); const key = `item-done:${itemId}`;
    if (busy[key] || item._optimistic) return;
    const done = !item.done;
    setError(''); setOperation(key, true);
    updateList(listId, (list) => ({ ...list, items: list.items.map((current) => idOf(current) === itemId ? { ...current, done } : current) }));
    try {
      const result = await apiUpdateTodoItem(jwtToken, listId, itemId, { done });
      updateList(listId, (list) => ({ ...list, items: list.items.map((current) => idOf(current) === itemId ? { ...current, ...result.item } : current) }));
    } catch (err) {
      updateList(listId, (list) => ({ ...list, items: list.items.map((current) => idOf(current) === itemId ? { ...current, done: item.done } : current) }));
      setError(err.message || 'The completion status could not be saved.');
    } finally { setOperation(key, false); }
  };

  const reassign = async (listId, item, assignedTo) => {
    const itemId = idOf(item); const key = `item-assign:${itemId}`;
    if (busy[key] || item._optimistic) return;
    const previous = { assignedTo: item.assignedTo || null, assignedToName: item.assignedToName || '' };
    const person = team.find((member) => idOf(member.id || member) === idOf(assignedTo));
    setError(''); setOperation(key, true);
    updateList(listId, (list) => ({ ...list, items: list.items.map((current) => idOf(current) === itemId
      ? { ...current, assignedTo: assignedTo || null, assignedToName: person?.name || '' } : current) }));
    try {
      const result = await apiUpdateTodoItem(jwtToken, listId, itemId, { assignedTo: assignedTo || null });
      updateList(listId, (list) => ({ ...list, items: list.items.map((current) => idOf(current) === itemId ? { ...current, ...result.item } : current) }));
    } catch (err) {
      updateList(listId, (list) => ({ ...list, items: list.items.map((current) => idOf(current) === itemId ? { ...current, ...previous } : current) }));
      setError(err.message || 'The assignee could not be changed.');
    } finally { setOperation(key, false); }
  };

  const saveHeading = async (event) => {
    event.preventDefault();
    if (!headingEditor || headingEditor.saving) return;
    const title = headingEditor.value.trim();
    if (!title) return;
    const editor = headingEditor;
    const list = lists.find((candidate) => idOf(candidate) === editor.listId);
    const previousTitle = editor.type === 'list' ? list?.title : list?.sections?.find((section) => idOf(section) === editor.sectionId)?.title;
    setHeadingEditor((current) => ({ ...current, saving: true, error: '' }));
    if (editor.type === 'list') updateList(editor.listId, (current) => ({ ...current, title }));
    else updateList(editor.listId, (current) => ({ ...current, sections: current.sections.map((section) => idOf(section) === editor.sectionId ? { ...section, title } : section) }));
    try {
      if (editor.type === 'list') await apiUpdateTodoList(jwtToken, editor.listId, { title });
      else await apiUpdateTodoSection(jwtToken, editor.listId, editor.sectionId, { title });
      setHeadingEditor(null);
    } catch (err) {
      if (editor.type === 'list') updateList(editor.listId, (current) => ({ ...current, title: previousTitle }));
      else updateList(editor.listId, (current) => ({ ...current, sections: current.sections.map((section) => idOf(section) === editor.sectionId ? { ...section, title: previousTitle } : section) }));
      setHeadingEditor((current) => ({ ...current, saving: false, error: err.message || 'Could not save this heading.' }));
    }
  };

  const openTaskEditor = (list, item) => setTaskEditor({
    listId: idOf(list), itemId: idOf(item), text: item.text, assignedTo: idOf(item.assignedTo),
    sectionId: idOf(item.sectionId), saving: false, error: '',
  });
  const saveTask = async (event) => {
    event.preventDefault();
    if (!taskEditor || taskEditor.saving || !taskEditor.text.trim()) return;
    const editor = taskEditor;
    const list = lists.find((candidate) => idOf(candidate) === editor.listId);
    const previous = list?.items?.find((item) => idOf(item) === editor.itemId);
    if (!previous) return;
    const person = team.find((member) => idOf(member.id || member) === idOf(editor.assignedTo));
    const optimistic = { text: editor.text.trim(), assignedTo: editor.assignedTo || null, assignedToName: person?.name || '', sectionId: editor.sectionId || null };
    setTaskEditor((current) => ({ ...current, saving: true, error: '' }));
    updateList(editor.listId, (current) => ({ ...current, items: current.items.map((item) => idOf(item) === editor.itemId ? { ...item, ...optimistic } : item) }));
    try {
      const result = await apiUpdateTodoItem(jwtToken, editor.listId, editor.itemId, optimistic);
      updateList(editor.listId, (current) => ({ ...current, items: current.items.map((item) => idOf(item) === editor.itemId ? { ...item, ...result.item } : item) }));
      setTaskEditor(null);
    } catch (err) {
      updateList(editor.listId, (current) => ({ ...current, items: current.items.map((item) => idOf(item) === editor.itemId ? previous : item) }));
      setTaskEditor((current) => ({ ...current, saving: false, error: err.message || 'The to-do could not be saved.' }));
    }
  };

  const requestRemoveItem = (list, item) => {
    const listId = idOf(list); const itemId = idOf(item);
    const itemIndex = list.items.findIndex((candidate) => idOf(candidate) === itemId);
    setConfirm({
      title: 'Archive to-do', message: `Archive “${item.text}”? Its discussion will be kept.`, confirmLabel: 'Archive', eyebrow: 'Archive item', isDangerous: true,
      onCancel: () => setConfirm(null),
      onConfirm: async () => {
        setConfirm((current) => ({ ...current, isLoading: true, loadingLabel: 'Archiving…' }));
        updateList(listId, (current) => ({ ...current, items: current.items.filter((candidate) => idOf(candidate) !== itemId) }));
        try { await apiDeleteTodoItem(jwtToken, listId, itemId); setConfirm(null); }
        catch (err) {
          updateList(listId, (current) => {
            if (current.items.some((candidate) => idOf(candidate) === itemId)) return current;
            const items = [...current.items];
            items.splice(Math.max(0, itemIndex), 0, item);
            return { ...current, items };
          });
          setConfirm(null); setError(err.message || 'The to-do could not be archived.');
        }
      },
    });
  };
  const requestRemoveSection = (list, section) => {
    const listId = idOf(list); const sectionId = idOf(section);
    const sectionIndex = list.sections.findIndex((candidate) => idOf(candidate) === sectionId);
    const affectedItemIds = new Set(list.items.filter((item) => idOf(item.sectionId) === sectionId).map(idOf));
    setConfirm({
      title: 'Archive section', message: `Archive “${section.title}”? Its to-dos will be moved to General.`, confirmLabel: 'Archive section', eyebrow: 'Archive group', isDangerous: true,
      onCancel: () => setConfirm(null),
      onConfirm: async () => {
        setConfirm((current) => ({ ...current, isLoading: true, loadingLabel: 'Archiving…' }));
        updateList(listId, (current) => ({ ...current, sections: current.sections.filter((candidate) => idOf(candidate) !== sectionId), items: current.items.map((item) => idOf(item.sectionId) === sectionId ? { ...item, sectionId: null } : item) }));
        try { await apiDeleteTodoSection(jwtToken, listId, sectionId); setConfirm(null); }
        catch (err) {
          updateList(listId, (current) => {
            const sections = [...current.sections];
            if (!sections.some((candidate) => idOf(candidate) === sectionId)) sections.splice(Math.max(0, sectionIndex), 0, section);
            return {
              ...current,
              sections,
              items: current.items.map((item) => affectedItemIds.has(idOf(item)) ? { ...item, sectionId } : item),
            };
          });
          setConfirm(null); setError(err.message || 'The section could not be archived.');
        }
      },
    });
  };
  const requestRemoveList = (list) => {
    const listId = idOf(list); const listIndex = lists.findIndex((candidate) => idOf(candidate) === listId);
    setConfirm({
      title: 'Archive list', message: `Archive “${list.title}”? Its to-dos and discussions will be kept in the database.`, confirmLabel: 'Archive list', eyebrow: 'Archive list', isDangerous: true,
      onCancel: () => setConfirm(null),
      onConfirm: async () => {
        setConfirm((current) => ({ ...current, isLoading: true, loadingLabel: 'Archiving…' }));
        setLists((current) => current.filter((candidate) => idOf(candidate) !== listId));
        try { await apiDeleteTodoList(jwtToken, listId); setConfirm(null); }
        catch (err) {
          setLists((current) => { const next = [...current]; next.splice(Math.max(0, listIndex), 0, list); return next; });
          setConfirm(null); setError(err.message || 'The list could not be archived.');
        }
      },
    });
  };

  const editorList = useMemo(() => taskEditor ? lists.find((list) => idOf(list) === taskEditor.listId) : null, [lists, taskEditor]);
  if (loading) return <div className="todo-loading"><span className="spinner" /><span>Loading your to-dos…</span></div>;
  if (loadError) return (
    <div className="todo-load-error card"><AlertCircle size={24} /><strong>Couldn’t load the to-do board</strong><span>{loadError}</span><button type="button" className="btn btn-primary btn-sm" onClick={loadBoard}>Try again</button></div>
  );

  return (
    <div className="todo-board">
      <div className="todo-page-heading">
        <span className="todo-page-icon"><CheckSquare size={19} /></span>
        <div><div className="todo-title-line"><h2>To-dos</h2><span className="badge badge-low">Team only</span></div><p>Organize work into lists and sections, assign owners, and discuss each to-do.</p></div>
      </div>
      {error && <div className="todo-error" role="alert"><AlertCircle size={16} /><span>{error}</span><button type="button" onClick={() => setError('')} aria-label="Dismiss error"><X size={15} /></button></div>}
      <form className="todo-new-list" onSubmit={createList}>
        <input className="input" maxLength={160} placeholder="New list, e.g. Social media" value={newListTitle} onChange={(event) => setNewListTitle(event.target.value)} aria-label="New list title" />
        <button type="submit" className="btn btn-primary" disabled={addingList || !newListTitle.trim()}>{addingList ? <span className="spinner" /> : <Plus size={16} />}{addingList ? 'Creating…' : 'Create list'}</button>
      </form>

      {lists.length === 0 ? <div className="todo-empty card"><Layers3 size={34} /><strong>No lists yet</strong><span>Create a list above, then add sections and to-dos.</span></div> : (
        <div className="todo-lists">
          {lists.map((list) => {
            const listId = idOf(list);
            const draft = drafts[listId] || { text: '', assignedTo: '', sectionId: '' };
            const sections = list.sections || [];
            const validSectionIds = new Set(sections.map(idOf));
            const generalItems = (list.items || []).filter((item) => !validSectionIds.has(idOf(item.sectionId)));
            const openCount = (list.items || []).filter((item) => !item.done).length;
            const editingList = headingEditor?.type === 'list' && headingEditor.listId === listId;
            const addingSection = busy[`section-add:${listId}`];
            const addingItem = busy[`item-add:${listId}`];
            const renderItems = (items) => items.length === 0 ? <div className="todo-section-empty">No to-dos in this section yet.</div> : items.map((item) => {
              const itemId = idOf(item); const savingDone = busy[`item-done:${itemId}`]; const savingAssignee = busy[`item-assign:${itemId}`];
              return (
                <div key={itemId} className={`todo-row${item.done ? ' is-done' : ''}${item._optimistic ? ' is-optimistic' : ''}`} data-todo-item={itemId}>
                  <button type="button" className="todo-check" onClick={() => toggleDone(listId, item)} disabled={savingDone || item._optimistic} aria-label={item.done ? `Mark ${item.text} as not done` : `Mark ${item.text} as done`} aria-pressed={item.done}>
                    {savingDone ? <span className="spinner" /> : item.done ? <Check size={13} strokeWidth={3} /> : null}
                  </button>
                  <button type="button" className="todo-text" onClick={() => !item._optimistic && openTaskEditor(list, item)} title="Edit to-do"><span>{item.text}</span>{item._optimistic && <small><span className="spinner" /> Creating…</small>}</button>
                  <div className="todo-row-controls">
                    <label className={`todo-assignee${savingAssignee ? ' is-saving' : ''}`} title="Change assignee">
                      <select value={idOf(item.assignedTo)} onChange={(event) => reassign(listId, item, event.target.value)} disabled={savingAssignee || item._optimistic} aria-label={`Assignee for ${item.text}`}>
                        <option value="">Unassigned</option>{team.map((member) => <option key={idOf(member.id || member)} value={idOf(member.id || member)}>{member.name}</option>)}
                      </select>{savingAssignee && <span className="spinner" />}
                    </label>
                    <button type="button" className="todo-icon-button" onClick={() => openTaskEditor(list, item)} disabled={item._optimistic} title="Edit to-do" aria-label={`Edit ${item.text}`}><Pencil size={14} /></button>
                    <button type="button" className={`todo-comment-button ${item.messageCount ? 'has-activity' : ''}`} onClick={() => setCommentTarget({ list, item })} disabled={item._optimistic} title="Open discussion">{item.lastCommenter ? <span className="todo-comment-avatar"><PersonAvatar person={item.lastCommenter} size="xs" /><i /></span> : <MessageCircle size={15} />}<span>{item.messageCount || 'Discuss'}</span></button>
                    {canDeleteTeamContent(user, item) && <button type="button" className="todo-icon-button danger" onClick={() => requestRemoveItem(list, item)} disabled={item._optimistic} title="Archive to-do" aria-label={`Archive ${item.text}`}><Trash2 size={14} /></button>}
                  </div>
                </div>
              );
            });

            return (
              <article key={listId} className={`todo-list-card card${list._optimistic ? ' is-optimistic' : ''}`} data-todo-list={listId}>
                <header className="todo-list-header">
                  <div className="todo-list-heading">
                    {editingList ? (
                      <form className="todo-heading-editor" onSubmit={saveHeading}>
                        <input className="input" value={headingEditor.value} onChange={(event) => setHeadingEditor((current) => ({ ...current, value: event.target.value, error: '' }))} maxLength={160} autoFocus aria-label="List title" />
                        <button type="submit" className="todo-icon-button success" disabled={headingEditor.saving || !headingEditor.value.trim()} aria-label="Save list title">{headingEditor.saving ? <span className="spinner" /> : <Check size={15} />}</button>
                        <button type="button" className="todo-icon-button" onClick={() => setHeadingEditor(null)} disabled={headingEditor.saving} aria-label="Cancel editing"><X size={15} /></button>{headingEditor.error && <small>{headingEditor.error}</small>}
                      </form>
                    ) : (
                      <div className="todo-list-title-row"><h3>{list.title}</h3><span>{openCount} open · {(list.items || []).length} total</span>{list._optimistic && <small><span className="spinner" /> Creating…</small>}{!list._optimistic && <button type="button" className="todo-icon-button" onClick={() => setHeadingEditor({ type: 'list', listId, value: list.title })} title="Rename list" aria-label={`Rename ${list.title}`}><Pencil size={14} /></button>}</div>
                    )}
                  </div>
                  <div className="todo-list-actions"><button type="button" className="btn btn-ghost btn-sm" onClick={() => setSectionForms((current) => ({ ...current, [listId]: !current[listId] }))} disabled={list._optimistic}><FolderPlus size={14} /> Section</button>{canDeleteTeamContent(user, list) && <button type="button" className="todo-icon-button danger" onClick={() => requestRemoveList(list)} disabled={list._optimistic} title="Archive list" aria-label={`Archive ${list.title}`}><Trash2 size={15} /></button>}</div>
                </header>

                {sectionForms[listId] && (
                  <form className="todo-new-section" onSubmit={(event) => createSection(event, list)}><Layers3 size={15} /><input className="input" value={sectionDrafts[listId] || ''} onChange={(event) => setSectionDrafts((current) => ({ ...current, [listId]: event.target.value }))} placeholder="Section heading, e.g. First month" maxLength={120} autoFocus /><button type="submit" className="btn btn-primary btn-sm" disabled={addingSection || !String(sectionDrafts[listId] || '').trim()}>{addingSection ? <span className="spinner" /> : <Plus size={14} />}{addingSection ? 'Adding…' : 'Add section'}</button><button type="button" className="todo-icon-button" onClick={() => setSectionForms((current) => ({ ...current, [listId]: false }))} disabled={addingSection} aria-label="Cancel adding section"><X size={15} /></button></form>
                )}

                <div className="todo-sections">
                  {sections.map((section) => {
                    const sectionId = idOf(section);
                    const sectionItems = (list.items || []).filter((item) => idOf(item.sectionId) === sectionId);
                    const editingSection = headingEditor?.type === 'section' && headingEditor.listId === listId && headingEditor.sectionId === sectionId;
                    return (
                      <section key={sectionId} className={`todo-section${section._optimistic ? ' is-optimistic' : ''}`}>
                        <div className="todo-section-heading">
                          {editingSection ? (
                            <form className="todo-heading-editor" onSubmit={saveHeading}><input className="input" value={headingEditor.value} onChange={(event) => setHeadingEditor((current) => ({ ...current, value: event.target.value, error: '' }))} maxLength={120} autoFocus aria-label="Section title" /><button type="submit" className="todo-icon-button success" disabled={headingEditor.saving || !headingEditor.value.trim()} aria-label="Save section title">{headingEditor.saving ? <span className="spinner" /> : <Check size={14} />}</button><button type="button" className="todo-icon-button" onClick={() => setHeadingEditor(null)} disabled={headingEditor.saving} aria-label="Cancel editing"><X size={14} /></button>{headingEditor.error && <small>{headingEditor.error}</small>}</form>
                          ) : <><div><Layers3 size={14} /><h4>{section.title}</h4><span>{sectionItems.length}</span>{section._optimistic && <small><span className="spinner" /> Saving…</small>}</div>{!section._optimistic && <div className="todo-section-actions"><button type="button" className="todo-icon-button" onClick={() => setHeadingEditor({ type: 'section', listId, sectionId, value: section.title })} title="Rename section" aria-label={`Rename ${section.title}`}><Pencil size={13} /></button><button type="button" className="todo-icon-button danger" onClick={() => requestRemoveSection(list, section)} title="Archive section" aria-label={`Archive ${section.title}`}><Trash2 size={13} /></button></div>}</>}
                        </div>
                        <div className="todo-section-items">{renderItems(sectionItems)}</div>
                      </section>
                    );
                  })}
                  {(generalItems.length > 0 || sections.length === 0) && <section className="todo-section todo-general-section"><div className="todo-section-heading"><div><CheckSquare size={14} /><h4>General</h4><span>{generalItems.length}</span></div></div><div className="todo-section-items">{renderItems(generalItems)}</div></section>}
                </div>

                <form className="todo-add-row" onSubmit={(event) => addItem(event, list)}>
                  <input className="input todo-add-text" placeholder="Add a to-do…" value={draft.text || ''} onChange={(event) => setDrafts((current) => ({ ...current, [listId]: { ...draft, text: event.target.value } }))} maxLength={500} aria-label={`New to-do in ${list.title}`} />
                  <select className="input" value={draft.sectionId || ''} onChange={(event) => setDrafts((current) => ({ ...current, [listId]: { ...draft, sectionId: event.target.value } }))} aria-label="Section"><option value="">General</option>{sections.filter((section) => !section._optimistic).map((section) => <option key={idOf(section)} value={idOf(section)}>{section.title}</option>)}</select>
                  <select className="input" value={draft.assignedTo || ''} onChange={(event) => setDrafts((current) => ({ ...current, [listId]: { ...draft, assignedTo: event.target.value } }))} aria-label="Assignee"><option value="">Unassigned</option>{team.map((member) => <option key={idOf(member.id || member)} value={idOf(member.id || member)}>{member.name}</option>)}</select>
                  <button type="submit" className="btn btn-primary" disabled={addingItem || !String(draft.text || '').trim() || list._optimistic}>{addingItem ? <span className="spinner" /> : <UserPlus size={15} />}{addingItem ? 'Adding…' : 'Add'}</button>
                </form>
              </article>
            );
          })}
        </div>
      )}

      {taskEditor && editorList && (
        <ModalShell title="Edit to-do" subtitle={`Update the task, owner, or section in ${editorList.title}.`} eyebrow="To-do details" icon={Pencil} size="sm" onClose={taskEditor.saving ? () => {} : () => setTaskEditor(null)}>
          <form className="popup-form" onSubmit={saveTask}>
            {taskEditor.error && <div className="popup-error"><AlertCircle size={15} /><span>{taskEditor.error}</span></div>}
            <label className="popup-field"><span className="field-label">TO-DO</span><textarea className="input" value={taskEditor.text} onChange={(event) => setTaskEditor((current) => ({ ...current, text: event.target.value, error: '' }))} maxLength={500} autoFocus /></label>
            <div className="popup-form-grid">
              <label className="popup-field"><span className="field-label">SECTION</span><select className="input" value={taskEditor.sectionId} onChange={(event) => setTaskEditor((current) => ({ ...current, sectionId: event.target.value }))}><option value="">General</option>{(editorList.sections || []).filter((section) => !section._optimistic).map((section) => <option key={idOf(section)} value={idOf(section)}>{section.title}</option>)}</select></label>
              <label className="popup-field"><span className="field-label">ASSIGNEE</span><select className="input" value={taskEditor.assignedTo} onChange={(event) => setTaskEditor((current) => ({ ...current, assignedTo: event.target.value }))}><option value="">Unassigned</option>{team.map((member) => <option key={idOf(member.id || member)} value={idOf(member.id || member)}>{member.name}</option>)}</select></label>
            </div>
            <div className="modal-footer"><button type="button" className="btn btn-ghost" onClick={() => setTaskEditor(null)} disabled={taskEditor.saving}>Cancel</button><button type="submit" className="btn btn-primary" disabled={taskEditor.saving || !taskEditor.text.trim()}>{taskEditor.saving ? <><span className="spinner" /> Saving…</> : <><Check size={15} /> Save changes</>}</button></div>
          </form>
        </ModalShell>
      )}
      {commentTarget && <ModalShell title={commentTarget.item.text} subtitle={`Discussion in ${commentTarget.list.title}`} eyebrow="To-do discussion" icon={MessageCircle} size="lg" className="todo-comments-modal" bodyClassName="todo-comments-body" onClose={() => setCommentTarget(null)}><Thread threadType="todo" threadId={idOf(commentTarget.item)} height="min(62vh, 560px)" placeholder="Write a comment…" onMessageSent={() => updateList(idOf(commentTarget.list), (list) => ({ ...list, items: list.items.map((item) => idOf(item) === idOf(commentTarget.item) ? { ...item, messageCount: (item.messageCount || 0) + 1 } : item) }))} onMessageDeleted={() => updateList(idOf(commentTarget.list), (list) => ({ ...list, items: list.items.map((item) => idOf(item) === idOf(commentTarget.item) ? { ...item, messageCount: Math.max(0, (item.messageCount || 0) - 1) } : item) }))} /></ModalShell>}
      {confirm && <ConfirmDialog {...confirm} />}
    </div>
  );
}
